import {remCalc} from '../../_utils'

const primaryVariant = {
    maxHeight: 'none',
    bg: 'background.accent.default',
    borderRadius: 'large',
    color: 'foreground.neutral.inverse',
    gap: remCalc(6),

    _hover: {
        bg: 'background.accent.hover',
    },

    _focusVisible: {
        outline: 'unset',
        border: 'none',
        boxShadow:
            '0 1px 2px 0 var(--chakra-shadows-neutral-sm), 0 0 0 2px var(--chakra-colors-background-base-default), 0 0 0 4px var(--chakra-shadows-accent-lg)',
    },

    _disabled: {
        bg: 'background.accent.muted',
        color: 'foreground.neutral.inverse',
        _hover: {
            bg: 'background.accent.muted !important',
            color: 'foreground.neutral.inverse !important',
        },
    },

    _active: {
        bg: 'background.accent.default !important',
        color: 'foreground.neutral.inverse !important',
    },

    '& .chakra-button__icon': {
        marginInlineEnd: 0,
        marginInlineStart: 0,
    },
}

const outlineVariant = {
    maxHeight: 'none',
    borderRadius: 'large',
    bg: 'background.neutral.default',
    boxShadow:
        '0 0 0 1px var(--chakra-colors-border-neutral-highlight) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs), 0 0 0 1px var(--chakra-shadows-neutral-xs) !important',
    color: 'foreground.neutral.default',
    border: 'none',
    gap: remCalc(6),
    _hover: {
        bg: 'background.neutral.hover',
        textDecoration: 'none',
    },
    _loading: {
        bg: 'background.neutral.muted',
        _hover: {
            bg: 'background.neutral.muted !important',
        },
    },
    _focusVisible: {
        outline: 'unset',
        border: 'none',
        boxShadow:
            '0 0 0 1px var(--chakra-colors-background-base-default) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs), 0 0 0 2px var(--chakra-shadows-accent-lg) !important',
    },
    _disabled: {
        bg: 'transparent',
        color: 'foreground.neutral.dim',
        boxShadow: 'none !important',
        border: 'solid 1px var(--chakra-colors-border-neutral-default) !important',
    },
    _active: {
        bg: 'background.neutral.default !important',
    },
    '& .chakra-button__icon': {
        marginInlineEnd: 0,
        marginInlineStart: 0,
    },
}

const ghostVariant = {
    maxHeight: 'none',
    borderRadius: 'large',
    bg: 'transparent',
    color: 'foreground.neutral.default',
    gap: remCalc(6),
    _hover: {
        bg: 'background.base.under',
    },
    _loading: {
        bg: 'background.neutral.muted',
        _hover: {
            bg: 'background.neutral.muted !important',
        },
    },
    _focusVisible: {
        outline: 'unset',
        border: 'none',
        bg: 'background.neutral.hover',
        boxShadow:
            '0 1px 2px 0 var(--chakra-shadows-neutral-sm), 0 0 0 2px var(--chakra-colors-background-base-default), 0 0 0 4px var(--chakra-shadows-accent-lg) !important',
    },
    _disabled: {
        bg: 'background.neutral.muted !important',
        color: 'foreground.neutral.dim !important',
        boxShadow: 'none !important',
    },
    _active: {
        bg: 'transparent !important',
    },
    '& .chakra-button__icon': {
        marginInlineEnd: 0,
        marginInlineStart: 0,
    },
}

const primaryIconButtonOverrides = {
    minW: 'auto',
    minH: 'auto',
    _hover: {
        minW: 'auto',
        minH: 'auto',
        bg: 'background.accent.hover',
    },
    _loading: {
        bg: 'background.accent.muted',
        color: 'foreground.accent.inverse',
        _hover: {
            bg: 'background.accent.muted !important',
        },
    },
    _active: {
        bg: 'background.accent.default !important',
    },
}

//Pass this to make a border fully rounded
const radiusFullOverride = {
    borderRadius: 'full',
}

const getLinkButtonVariant = (size: string, withUnderline = true) => {
    const sizeStyles = {
        small: {
            marginY: remCalc(4),
            padding: remCalc(0),
            textStyle: 'body-xsmall',
            fontWeight: 'medium',
            height: remCalc(20),
        },
        base: {
            marginY: remCalc(4),
            padding: remCalc(0),
            textStyle: 'body-small',
            fontWeight: 'medium',
            height: remCalc(24),
        },
        large: {
            marginY: remCalc(4),
            padding: remCalc(0),
            textStyle: 'body-medium',
            fontWeight: 'medium',
            height: remCalc(28),
        },
    }

    const underlineStyles = withUnderline
        ? {
              backgroundImage:
                  'radial-gradient(ellipse 2px 1px at center, #C8C7C7 100%, transparent 100%) !important',
              backgroundSize: '8px 3px !important',
              backgroundRepeat: 'repeat-x !important',
              backgroundPosition: 'bottom !important',

              _hover: {
                  backgroundImage:
                      'radial-gradient(ellipse 2px 1px at center, #13183A 100%, transparent 100%) !important',
              },
          }
        : {
              backgroundImage: 'none !important',
              backgroundSize: 'auto !important',
              backgroundRepeat: 'no-repeat !important',
              backgroundPosition: 'initial !important',
          }

    return {
        ...(sizeStyles[size as keyof typeof sizeStyles] || sizeStyles.base),
        color: 'foreground.neutral.muted',
        paddingBottom: remCalc(4),
        ...underlineStyles,
        _hover: {
            color: 'foreground.neutral.default',
            textDecoration: 'none',
            ...underlineStyles._hover,
            _disabled: {
                color: 'foreground.neutral.subtle',
                ...underlineStyles,
            },
        },
        _active: {
            color: 'foreground.neutral.muted !important',
        },
        _focusVisible: {
            color: 'foreground.neutral.default !important',
            outline: 'unset',
            border: 'none',
            borderRadius: 'large',
            boxShadow:
                '0 1px 2px 0 var(--chakra-colors-background-base-default), 0 0 0 2px var(--chakra-shadows-neutral-xs), 0 0 0 4px var(--chakra-shadows-accent-lg) !important',
        },
        _disabled: {
            color: 'foreground.neutral.subtle',
        },
    }
}

export default {
    sizes: {
        small: {
            paddingY: 'var(--chakra-sizes-button-small-paddingY)',
            paddingX: 'var(--chakra-sizes-button-small-paddingX)',
            textStyle: 'body-xsmall',
            fontWeight: 'medium',
            height: 'var(--chakra-sizes-button-small-height)',
        },
        base: {
            paddingY: 'var(--chakra-sizes-button-base-paddingY)',
            paddingX: 'var(--chakra-sizes-button-base-paddingX)',
            textStyle: 'body-small',
            fontWeight: 'medium',
            height: 'var(--chakra-sizes-button-base-height)',
        },
        large: {
            paddingY: 'var(--chakra-sizes-button-large-paddingY)',
            paddingX: 'var(--chakra-sizes-button-large-paddingX)',
            textStyle: 'body-small',
            fontWeight: 'medium',
            height: 'var(--chakra-sizes-button-large-height)',
        },
        xl: {
            paddingY: 'var(--chakra-sizes-button-xl-paddingY)',
            paddingX: 'var(--chakra-sizes-button-xl-paddingX)',
            textStyle: 'body-medium',
            fontWeight: 'medium',
            height: 'var(--chakra-sizes-button-xl-height)',
        },
    },

    baseStyle: {
        borderRadius: 'none',
        display: 'inline-flex',
        maxW: '100%',
        outlineWidth: 0,
        textStyle: 'body-medium',
        fontWeight: 'bold',
        alignItems: 'center',
        textWrap: 'auto',

        _hover: {
            textDecoration: 'none',
        },
        _focusVisible: {
            border: remCalc(3),
            borderStyle: 'solid',
            borderColor: 'button.border.accessibility-blue-dark',
            outline: 'unset',
            boxShadow: 'unset',
        },
        _disabled: {
            opacity: 1,
        },
    },
    variants: {
        //These are the NEW variants of the buttons
        primary: {
            ...primaryVariant,
            ...radiusFullOverride,
        },
        outline: {
            ...outlineVariant,
            ...radiusFullOverride,
        },
        ghost: {
            ...ghostVariant,
            ...radiusFullOverride,
        },

        'icon-button-primary': {
            ...primaryVariant,
            ...primaryIconButtonOverrides,
        },
        'icon-button-outline': {
            ...outlineVariant,
        },
        'icon-button-ghost': {
            ...ghostVariant,
        },

        'primary-not-rounded': {
            ...primaryVariant,
        },
        'outline-not-rounded': {
            ...outlineVariant,
        },
        'ghost-not-rounded': {
            ...ghostVariant,
        },

        'link-button': ({size}: {size: string}) => getLinkButtonVariant(size, true),
        'link-button-no-underline': ({size}: {size: string}) => getLinkButtonVariant(size, false),

        //These are the OLD button variants that we are keeping for backwards compatibility

        'primary-small': {
            textStyle: 'body-xsmall',
            padding: `${remCalc(12)} ${remCalc(16)}`,
            maxHeight: remCalc(33),
            bg: 'button.background.primary-default',
            borderRadius: [
                'button.radius.primary-default.base',
                'button.radius.primary-default.sm',
                'button.radius.primary-default.md',
                'button.radius.primary-default.lg',
            ],
            color: 'button.text.primary-default',
            _hover: {
                bg: 'button.background.primary-hover',
            },
            _focusVisible: {
                border: remCalc(3),
                borderStyle: 'solid',
                borderColor: 'button.border.accessibility-blue-dark',
                outline: 'unset',
                boxShadow: 'unset',
            },
            _disabled: {
                bg: 'button.background.primary-disabled',
                color: 'button.text.primary-disabled',
                _hover: {
                    bg: 'button.background.primary-disabled !important',
                    color: 'button.text.primary-disabled',
                },
            },
            _active: {
                bg: 'button.background.primary-default', // when a button is clicked, we use default colors
                color: 'button.text.primary-default',
            },
            _pressed: {
                bg: 'button.background.primary-pressed',
                color: 'button.text.primary-pressed',
            },
        },
        secondary: {
            padding: `${remCalc(18)} ${remCalc(24)}`,
            maxHeight: remCalc(40),
            bg: 'button.background.secondary-default',
            color: 'button.text.secondary-default',
            border: remCalc(1),
            borderColor: 'button.border.secondary-default',
            borderStyle: 'solid',
            borderRadius: [
                'button.radius.primary-default.base',
                'button.radius.primary-default.sm',
                'button.radius.primary-default.md',
                'button.radius.primary-default.lg',
            ],
            _hover: {
                border: 'none',
                color: 'button.text.secondary-hover',
                bg: 'button.background.primary-default',
            },
            _focusVisible: {
                border: remCalc(3),
                borderStyle: 'solid',
                borderColor: 'button.border.accessibility-blue-dark',
                outline: 'unset',
                boxShadow: 'unset',
            },
            _active: {
                bg: 'button.background.secondary-focused',
                color: 'button.text.secondary-default',
                border: remCalc(1),
                borderColor: 'button.border.secondary-default',
                borderStyle: 'solid',
            },
            _disabled: {
                color: 'button.text.secondary-disabled',
                bg: 'button.background.secondary-disabled',
                borderColor: 'button.border.secondary-disabled',
                _hover: {
                    bg: 'button.background.secondary-disabled',
                    color: 'button.text.secondary-disabled',
                    border: remCalc(1),
                    borderColor: 'button.border.secondary-disabled',
                    borderStyle: 'solid',
                },
            },
            _pressed: {
                bg: 'button.background.secondary-pressed',
                color: 'button.text.secondary-pressed',
                borderColor: 'button.border.secondary-pressed',
            },
        },
        'secondary-small': {
            textStyle: 'body-xsmall',
            padding: `${remCalc(12)} ${remCalc(16)}`,
            maxHeight: remCalc(33),
            bg: 'button.background.secondary-default',
            color: 'button.text.secondary-default',
            border: remCalc(1),
            borderColor: 'button.border.secondary-default',
            borderStyle: 'solid',
            borderRadius: [
                'button.radius.primary-default.base',
                'button.radius.primary-default.sm',
                'button.radius.primary-default.md',
                'button.radius.primary-default.lg',
            ],
            _hover: {
                border: 'none',
                color: 'button.text.secondary-hover',
                bg: 'button.background.secondary-hover',
            },
            _focusVisible: {
                border: remCalc(3),
                borderStyle: 'solid',
                borderColor: 'button.border.accessibility-blue-dark',
                outline: 'unset',
                boxShadow: 'unset',
            },
            _active: {
                bg: 'button.background.secondary-default',
                color: 'button.text.secondary-default',
            },
            _disabled: {
                color: 'button.text.secondary-disabled',
                bg: 'button.background.secondary-disabled',
                borderColor: 'button.border.secondary-disabled',
                _hover: {
                    bg: 'button.background.secondary-disabled',
                    color: 'button.text.secondary-disabled',
                    border: remCalc(1),
                    borderColor: 'button.border.secondary-disabled',
                    borderStyle: 'solid',
                },
            },
            _pressed: {
                bg: 'button.background.secondary-pressed',
                color: 'button.text.secondary-pressed',
                borderColor: 'button.border.secondary-pressed',
            },
        },
        'icon-button': {
            bg: 'button.background.icon-default',
            color: 'button.text.icon-default',
            _hover: {color: 'button.text.icon-hover'},
            _disabled: {color: 'button.text.icon-disabled'},
            _pressed: {color: 'button.text.icon-pressed'},
        },
        'text-button': {
            bg: 'button.background.text-default',
            color: 'button.text.text-default',
            textDecoration: 'underline',
            _disabled: {color: 'button.text.text-disabled'},
            _focusVisible: {
                border: remCalc(3),
                borderStyle: 'solid',
                borderColor: 'button.border.accessibility-blue-dark',
                outline: 'unset',
                boxShadow: 'unset',
            },
        },
        'primary-pressed': {
            whiteSpace: 'normal',
            padding: `${remCalc(18)} ${remCalc(24)}`,
            maxHeight: remCalc(40),
            borderRadius: [
                'button.radius.primary-default.base',
                'button.radius.primary-default.sm',
                'button.radius.primary-default.md',
                'button.radius.primary-default.lg',
            ],
            bg: 'button.background.inverse-primary-pressed',
            color: 'button.text.inverse-primary-pressed',
            borderColor: 'button.border.secondary-pressed',
            _hover: {
                bg: 'button.background.inverse-primary-pressed',
                color: 'button.text.inverse-primary-pressed',
                borderColor: 'button.border.secondary-pressed',
                _disabled: {
                    bg: 'button.background.inverse-primary-pressed',
                    color: 'button.text.inverse-primary-pressed',
                    borderColor: 'button.border.secondary-pressed',
                },
            },
        },
        'primary-pressed-small': {
            whiteSpace: 'normal',
            textStyle: 'body-xsmall',
            padding: `${remCalc(12)} ${remCalc(16)}`,
            maxHeight: remCalc(33),
            borderRadius: [
                'button.radius.primary-default.base',
                'button.radius.primary-default.sm',
                'button.radius.primary-default.md',
                'button.radius.primary-default.lg',
            ],
            bg: 'button.background.inverse-primary-pressed',
            color: 'button.text.inverse-primary-pressed',
            borderColor: 'button.border.secondary-pressed',
        },
        //* Inverse styles
        'inverse-primary': {
            padding: `${remCalc(18)} ${remCalc(24)}`,
            maxHeight: remCalc(40),
            borderRadius: [
                'button.radius.inverse-primary-default.base',
                'button.radius.inverse-primary-default.sm',
                'button.radius.inverse-primary-default.md',
                'button.radius.inverse-primary-default.lg',
            ],
            bg: 'button.background.inverse-primary-default',
            color: 'button.text.inverse-primary-default',
            _hover: {
                bg: 'button.background.inverse-primary-hover',
                color: 'button.text.inverse-primary-hover',
            },
            _focusVisible: {
                border: remCalc(3),
                borderStyle: 'solid',
                borderColor: 'button.border.accessibility-blue-dark',
                outline: 'unset',
                boxShadow: 'unset',
            },
            _active: {
                bg: 'button.background.inverse-primary-default',
                color: 'button.text.inverse-primary-default',
            },
            _disabled: {
                color: 'button.text.inverse-primary-disabled',
                bg: 'button.background.inverse-primary-disabled',
                _hover: {
                    bg: 'button.background.inverse-primary-disabled !important',
                    color: 'button.text.inverse-primary-disabled',
                },
            },
            _pressed: {
                color: 'button.text.inverse-primary-pressed',
                bg: 'button.background.inverse-primary-pressed',
            },
        },
        'inverse-secondary': {
            padding: `${remCalc(18)} ${remCalc(24)}`,
            maxHeight: remCalc(40),
            borderRadius: [
                'button.radius.inverse-secondary-default.base',
                'button.radius.inverse-secondary-default.sm',
                'button.radius.inverse-secondary-default.md',
                'button.radius.inverse-secondary-default.lg',
            ],
            bg: 'button.background.inverse-secondary-default',
            color: 'button.text.inverse-secondary-default',
            border: remCalc(2),
            borderColor: 'button.border.inverse-secondary-default',
            borderStyle: 'solid',
            _hover: {
                border: 'none',
                color: 'button.text.inverse-secondary-hover',
                bg: 'button.background.inverse-secondary-hover',
            },
            _focus: {
                borderColor: 'button.border.inverse-secondary-focused',
                color: 'button.text.inverse-secondary-focused',
                bg: 'button.background.inverse-secondary-focused',
            },
            _active: {
                bg: 'button.background.inverse-secondary-default',
                color: 'button.text.inverse-secondary-default',
                border: remCalc(2),
                borderColor: 'button.border.inverse-secondary-default',
                borderStyle: 'solid',
            },
            _disabled: {
                bg: 'button.background.inverse-secondary-disabled',
                borderColor: 'button.border.inverse-secondary-disabled',
                _hover: {
                    bg: 'button.background.inverse-secondary-disabled',
                    color: 'button.text.inverse-secondary-disabled !important',
                    border: remCalc(2),
                    borderColor: 'button.border.inverse-secondary-disabled',
                },
            },
            _pressed: {
                border: remCalc(1),
                bg: 'button.background.inverse-secondary-pressed',
                color: 'button.text.inverse-secondary-pressed',
                borderColor: 'button.border.inverse-secondary-pressed',
                borderStyle: 'solid',
            },
        },
        'inverse-icon-button': {
            bg: 'button.background.inverse-icon-default',
            color: 'button.text.inverse-icon-default',
            _hover: {color: 'button.text.inverse-icon-hover'},
            _disabled: {
                color: 'button.text.inverse-icon-default',
                cursor: 'not-allowed',
                _hover: {
                    color: 'button.text.inverse-icon-default',
                    textDecoration: 'none',
                    cursor: 'not-allowed',
                },
            },
            _pressed: {color: 'button.text.inverse-icon-pressed'},
        },
        'inverse-text-button': {
            bg: 'button.background.inverse-text-default',
            color: 'button.text.inverse-text-default',
            textDecoration: 'underline',
            _disabled: {color: 'button.text.inverse-text-disabled'},
        },
        'inverted-pressed': {
            padding: `${remCalc(18)} ${remCalc(24)}`,
            maxHeight: remCalc(40),
            border: remCalc(2),
            borderStyle: 'solid',
            borderRadius: [
                'button.radius.primary-default.base',
                'button.radius.primary-default.sm',
                'button.radius.primary-default.md',
                'button.radius.primary-default.lg',
            ],
            bg: 'button.background.secondary-pressed',
            color: 'button.text.secondary-pressed',
            borderColor: 'button.border.secondary-pressed',
        },
        'filter-button': {
            bg: 'background.base.under',
            color: 'foreground.neutral.default',
            paddingY: {
                base: 'xxsmall.base',
                sm: 'xxsmall.base',
                md: 'xxsmall.lg',
                lg: 'xxsmall.lg',
            }, //8 - 6
            paddingX: {
                base: 'xxsmall-1.base',
                sm: 'xxsmall-1.base',
                md: 'sxmamll.lg',
                lg: 'xxsmall-1.lg',
            }, //12 -8
            borderRadius: 'full',
            minHeight: remCalc(32),
            textStyle: 'body-small',
            fontWeight: 'medium',
            maxHeight: remCalc(32),
            gap: 'xxxsmall-1.base',

            '& .chakra-button__icon': {
                marginInlineEnd: 0,
                marginInlineStart: 0,
            },

            boxShadow:
                '0 0 0 1px var(--chakra-colors-border-neutral-highlight) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs), 0 0 0 1px var(--chakra-shadows-neutral-xs)',

            _pressed: {
                bg: 'background.strong.default',
                color: 'border.neutral.highlight',
            },

            _disabled: {
                background: 'background.base.under',

                _hover: {
                    background: 'background.base.under',
                    boxShadow: 'none',
                },
            },
        },
    },
    defaultProps: {colorScheme: 'unset', variant: 'primary', size: 'large'},
}
